/**
 * Orka V3 — Task identity maps (approved full-bleed-header PRD).
 *
 * Single source of truth for the task-card header's five signal systems:
 *
 *   PROJECT_V3    — workstream identity (colour, categories)
 *   PRIORITY_V3   — independent priority signal
 *   STATE_V3      — task state (open, inProgress, partial, blocked, done)
 *   INTENT_V3     — user intent (focus, plan, recover, commit, private, system)
 *
 * PROJECT_V3 extends `design-system/projects.js` with a static `categories`
 * array (chip row inside the colour field).
 */
import { PROJECT_META as BASE_PROJECTS } from './projects';

export const PROJECT_V3 = Object.freeze({
  ...BASE_PROJECTS,
  // legacy/unmapped — fallback identity
  unknown: {
    key: 'unknown',
    label: 'Untagged',
    short: 'TBD',
    primary: '#64748b',
    secondary: '#475569',
    tertiary: '#94a3b8',
    text: '#f8fafc',
    categories: [],
  },
});

export function resolveProject(task) {
  if (!task) return PROJECT_V3.unknown;
  // Direct color override — populated by callers that already know the
  // work-area colour (e.g. DailyDashboard2 enriches each task with
  // `project_color_hex` derived from useWorkAreas().workAreaColors).
  const directColor = task.project_color_hex || task.work_area_color || task.task_work_area_color;
  if (directColor) {
    const label = task.work_area_name || task.task_project || 'Workstream';
    const short = (label || '·').split(/\s+/).map(s => s[0]).join('').slice(0, 3).toUpperCase() || '·';
    return {
      key: `wa:${label}`,
      label,
      short,
      primary: directColor,
      secondary: directColor,
      tertiary: directColor,
      text: '#ffffff',
      categories: [],
    };
  }
  // task_project (legacy string like "Billing / SaaS"), or task.projectKey
  const raw = (task.projectKey || task.task_project || '').toString().toLowerCase();
  if (!raw) return PROJECT_V3.unknown;
  // Exact match on our map keys
  if (PROJECT_V3[raw]) return PROJECT_V3[raw];
  // Contains matches to map legacy labels
  if (raw.includes('bill') || raw.includes('saas') || raw.includes('stripe')) return PROJECT_V3.billing;
  if (raw.includes('identity') || raw.includes('sso') || raw.includes('auth')) return PROJECT_V3.identity;
  if (raw.includes('growth') || raw.includes('marketing') || raw.includes('trial')) return PROJECT_V3.growth;
  if (raw.includes('ops') || raw.includes('admin') || raw.includes('payroll') || raw.includes('finance')) return PROJECT_V3.ops;
  if (raw.includes('risk') || raw.includes('security') || raw.includes('audit')) return PROJECT_V3.risk;
  return PROJECT_V3.unknown;
}

// Independent priority signal. Colour comes from the canonical PRIORITY_META
// CSS variables — NEVER hardcode priority hex here (Feb 2026 token migration).
export const PRIORITY_V3 = Object.freeze({
  '-1': { key: '-1', label: 'P-1', long: 'P-1 Emergency', color: 'var(--orka-priority-emergency)' },
  '0':  { key: '0',  label: 'P0',  long: 'P0 Fixed',      color: 'var(--orka-priority-fixed)' },
  '1':  { key: '1',  label: 'P1',  long: 'P1 High',       color: 'var(--orka-priority-high)' },
  '2':  { key: '2',  label: 'P2',  long: 'P2 Medium',     color: 'var(--orka-priority-medium)' },
  '3':  { key: '3',  label: 'P3',  long: 'P3 Low',        color: 'var(--orka-priority-low)' },
  '4':  { key: '4',  label: 'P4',  long: 'P4 Later',      color: 'var(--orka-priority-later)' },
  '5':  { key: '5',  label: 'P5',  long: 'P5 Someday',    color: 'var(--orka-priority-someday)' },
});

export function resolvePriority(task) {
  const raw = task?.task_priority;
  if (raw === null || raw === undefined) return PRIORITY_V3['3'];
  return PRIORITY_V3[String(raw)] || PRIORITY_V3['3'];
}

// Task state — maps backend task_status strings into V3 state tokens.
// Colour now flows from the canonical TASK_STATE_META CSS variables.
export const STATE_V3 = Object.freeze({
  open:       { key: 'open',       label: 'Open',        short: 'Open',    color: 'var(--orka-state-open)' },
  inProgress: { key: 'inProgress', label: 'In progress', short: 'Active',  color: 'var(--orka-state-in-progress)' },
  partial:    { key: 'partial',    label: 'Partial',     short: 'Partial', color: 'var(--orka-state-partial)' },
  blocked:    { key: 'blocked',    label: 'Blocked',     short: 'Blocked', color: 'var(--orka-state-blocked)' },
  done:       { key: 'done',       label: 'Done',        short: 'Done',    color: 'var(--orka-state-done)' },
});

export function resolveState(task) {
  // Prefer canonical task_status_key (post-cleanup); fall back to legacy task_status string.
  const canonicalKey = task?.task_status_key;
  if (canonicalKey && STATE_V3[canonicalKey]) return STATE_V3[canonicalKey];
  const raw = (task?.task_status || 'To Do').toString();
  if (raw === 'In Progress') return STATE_V3.inProgress;
  if (raw === 'Partial') return STATE_V3.partial;
  if (raw === 'Blocked') return STATE_V3.blocked;
  if (raw === 'Done') return STATE_V3.done;
  return STATE_V3.open;
}

// Intent — user's intended action on this task right now.
export const INTENT_V3 = Object.freeze({
  focus:   { key: 'focus',   label: 'Focus',   color: '#2563eb' },
  plan:    { key: 'plan',    label: 'Plan',    color: '#64748b' },
  recover: { key: 'recover', label: 'Recover', color: '#d97706' },
  commit:  { key: 'commit',  label: 'Commit',  color: '#059669' },
  private: { key: 'private', label: 'Private', color: '#94a3b8' },
  system:  { key: 'system',  label: 'System',  color: '#475569' },
  capture: { key: 'capture', label: 'Capture', color: '#64748b' },
  warn:    { key: 'warn',    label: 'Warn',    color: '#dc2626' },
});

export function resolveIntent(task) {
  const raw = (task?.intent || task?.task_intent || '').toString();
  if (INTENT_V3[raw]) return INTENT_V3[raw];
  const state = resolveState(task);
  if (state.key === 'inProgress') return INTENT_V3.focus;
  if (state.key === 'partial' || state.key === 'blocked') return INTENT_V3.recover;
  if (state.key === 'done') return INTENT_V3.commit;
  return INTENT_V3.plan;
}

// ------------------------------------------------------------------------
// DB hydration — VocabularyProvider calls these on boot and on admin save.
// Each function mutates the existing frozen maps in place (Object.freeze is
// shallow so inner values remain writable). All consumers automatically
// pick up the new values on their next render (forced by the provider's
// version-keyed remount).
// ------------------------------------------------------------------------

export function hydratePriorityV3(rows) {
  if (!Array.isArray(rows)) return;
  rows.forEach((r) => {
    const m = PRIORITY_V3[r.key];
    if (!m) return;
    m.label = r.short_label || m.label;
    m.long = r.long_label || m.long;
    m.color = r.resolved_color || r.color || m.color;
  });
}

export function hydrateStateV3(rows) {
  if (!Array.isArray(rows)) return;
  rows.forEach((r) => {
    const m = STATE_V3[r.key];
    if (!m) return;
    m.label = r.label || m.label;
    m.short = r.short || m.short;
    m.color = r.resolved_color || r.color || m.color;
  });
}

export function hydrateIntentV3(rows) {
  if (!Array.isArray(rows)) return;
  rows.forEach((r) => {
    const m = INTENT_V3[r.key];
    if (!m) return;
    m.label = r.label || m.label;
    m.color = r.color || m.color;
  });
}
